import { findDexEntry, indexDex, toComparePokemon } from '../utils/dexLookup';
import { flavorFor } from '../utils/flavor';
import { summarizeMatchup, toQuery } from '../utils/pokemon';

const API_BASE = process.env.REACT_APP_API_URL || '';
const STATIC_DATA = process.env.REACT_APP_STATIC_DATA === '1';

async function request(path, options = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    const unavailable = new Error('The Pokémon service is unavailable. Is the API running?');
    unavailable.status = 503;
    throw unavailable;
  }

  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }

  if (!response.ok) {
    const failure = new Error(data.error || 'Something went wrong. Try again.');
    failure.status = response.status;
    throw failure;
  }

  return data;
}

function missingQuery(nameOrId) {
  const query = toQuery(nameOrId);
  if (query) return null;
  const raw = String(nameOrId ?? '').trim();
  const error = new Error(
    raw
      ? 'No Pokémon matches that name or number.'
      : 'Enter a Pokémon name or number.'
  );
  error.status = 400;
  return error;
}

async function publishedJson(path, options = {}) {
  let response;
  try {
    response = await fetch(path, {
      headers: { Accept: 'application/json' },
      signal: options.signal,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    const unavailable = new Error('The published Pokédex data could not be loaded.');
    unavailable.status = 503;
    throw unavailable;
  }
  const text = await response.text();
  if (!response.ok || text.trim().startsWith('<')) {
    const missing = new Error('That page is not in the published data.');
    missing.status = 404;
    throw missing;
  }
  return JSON.parse(text);
}

let publishedDexPromise = null;

function loadPublishedDex(options) {
  if (!publishedDexPromise) {
    publishedDexPromise = publishedJson('/data/dex.json', options)
      .then((dex) => ({ ...dex, byKey: indexDex(dex) }))
      .catch((error) => {
        publishedDexPromise = null;
        throw error;
      });
  }
  return publishedDexPromise;
}

export function getRandomPokemon(options) {
  if (!STATIC_DATA) return request('/api/pokemon/random', options);
  return loadPublishedDex(options).then((dex) => {
    const list = (dex.pokemon || []).filter((entry) => !entry.mega && !entry.primal);
    const entry = list[Math.floor(Math.random() * list.length)];
    if (!entry) {
      const error = new Error('The published Pokédex data could not be loaded.');
      error.status = 503;
      throw error;
    }
    return toComparePokemon(entry);
  });
}

function pokemonFromDex(dex, query) {
  const entry = findDexEntry(dex, query);
  return entry?.stats ? toComparePokemon(entry) : null;
}

export function getPokemon(nameOrId, options) {
  const rejected = missingQuery(nameOrId);
  if (rejected) return Promise.reject(rejected);
  const query = toQuery(nameOrId);
  if (STATIC_DATA) {
    return loadPublishedDex(options).then((dex) => {
      const pokemon = pokemonFromDex(dex, query);
      if (pokemon) return pokemon;
      const error = new Error('No Pokémon matches that name or number.');
      error.status = 404;
      throw error;
    });
  }
  return getDexIndex(options).then((dex) => {
    const pokemon = pokemonFromDex({ pokemon: dex.pokemon || [] }, query);
    if (pokemon) return pokemon;
    return request(`/api/pokemon/${encodeURIComponent(query)}`, options);
  });
}

export function comparePokemon(pokemon1Id, pokemon2Id, options) {
  if (!STATIC_DATA) {
    return request('/api/pokemon/compare', {
      method: 'POST',
      body: JSON.stringify({ pokemon1Id, pokemon2Id }),
      ...options,
    });
  }
  return Promise.all([
    getPokemon(pokemon1Id, options),
    getPokemon(pokemon2Id, options),
  ]).then(([pokemon1, pokemon2]) => ({
    pokemon1,
    pokemon2,
    summary: summarizeMatchup(pokemon1, pokemon2),
  }));
}

let dexIndexPromise = null;
const dexEntryCache = new Map();
let metaOverviewPromise = null;
const metaFormatCache = new Map();

export function getDexIndex({ fresh = false, ...options } = {}) {
  if (!fresh && dexIndexPromise) return dexIndexPromise;
  const load = STATIC_DATA
    ? loadPublishedDex(options).then((dex) => ({
      source: dex.source,
      count: (dex.pokemon || []).length,
      stale: Boolean(dex.stale),
      pokemon: dex.pokemon || [],
    }))
    : request('/api/pokedex', options);
  dexIndexPromise = load.catch((error) => {
    dexIndexPromise = null;
    throw error;
  });
  return dexIndexPromise;
}

export function getDexEntry(key, { fresh = false, ...options } = {}) {
  const id = encodeURIComponent(String(key));
  if (!fresh && dexEntryCache.has(id)) return dexEntryCache.get(id);
  const load = STATIC_DATA
    ? loadPublishedDex(options).then(async (dex) => {
      const entry = findDexEntry(dex, key);
      if (!entry?.stats) {
        const error = new Error('No entry matches that name or number.');
        error.status = 404;
        throw error;
      }
      const flavor = await flavorFor(entry.id, options.signal);
      return {
        pokemon: entry,
        flavorText: flavor?.text || null,
        flavorVersion: flavor?.version || null,
        stale: Boolean(dex.stale),
      };
    })
    : request(`/api/pokedex/${id}`, options);
  const pending = load.catch((error) => {
    dexEntryCache.delete(id);
    throw error;
  });
  dexEntryCache.set(id, pending);
  return pending;
}

export function getMetaOverview({ fresh = false, ...options } = {}) {
  if (!fresh && metaOverviewPromise) return metaOverviewPromise;
  const load = STATIC_DATA
    ? publishedJson('/data/meta.json', options)
    : request('/api/meta', options);
  metaOverviewPromise = load.catch((error) => {
    metaOverviewPromise = null;
    throw error;
  });
  return metaOverviewPromise;
}

const usageCache = new Map();

async function publishedUsage(key, options) {
  const raw = String(key || '').trim().toLowerCase();
  const flat = raw.replace(/[^a-z0-9]+/g, '');
  const keys = flat && flat !== raw ? [raw, flat] : [raw];
  let lastError = null;
  for (const name of keys) {
    try {
      return await publishedJson(`/data/usage/${encodeURIComponent(name)}.json`, options);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}

export function getUsage(key, { fresh = false, ...options } = {}) {
  const id = encodeURIComponent(String(key));
  if (!fresh && usageCache.has(id)) return usageCache.get(id);
  const load = STATIC_DATA
    ? publishedUsage(key, options)
    : request(`/api/usage/${id}`, options);
  const pending = load.catch((error) => {
    usageCache.delete(id);
    throw error;
  });
  usageCache.set(id, pending);
  return pending;
}

export function getMetaFormat(formatId, { fresh = false, ...options } = {}) {
  const id = encodeURIComponent(String(formatId));
  if (!fresh && metaFormatCache.has(id)) return metaFormatCache.get(id);
  const load = STATIC_DATA
    ? publishedJson(`/data/formats/${id}.json`, options)
    : request(`/api/meta/${id}`, options);
  const pending = load.catch((error) => {
    metaFormatCache.delete(id);
    throw error;
  });
  metaFormatCache.set(id, pending);
  return pending;
}
